import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link } from "react-router-dom";
import { EditIcon } from "../../../assets/icons/Icons";
import Chip from "../../../components/Chip";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import SearchFilter from "../../../components/filters/SearchFilter";
import SelectFilter from "../../../components/filters/SelectFilter";
import ResetFilters from "../../../components/filters/ResetFilters";
import CreateRoleForm from "../../../components/role/Create";
import EditRoleForm from "../../../components/role/Edit";
import MessagePopup from "../../../components/MessagePopup";
import DataTable from "../../../components/DataTable";
import CustomTooltip from "../../../components/CustomTooltip";
import useData from "../../../hooks/useData";
import FilterRoles from "../../../api/roles/filterRoles";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useUserContext } from "../../../contexts/UserContext";
import { RBAC_RESOURCE_USER_ROLES } from "../../../contstants";

const Index = () => {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    newItemAdded,
    setNewItemAdded,
    itemUpdated,
    setItemUpdated,
    selectedItem,
    setSelectedItem,
    showEditItem,
    setShowEditItem,
    showCreateItem,
    setShowCreateItem,
  } = useData(FilterRoles);

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();
  const { hasPermission, hasDynamicPermissions, userData } = useUserContext();

  // Dynamic users need the write grant; legacy owner/admin manage by role.
  const canManageRoles =
    hasPermission(RBAC_RESOURCE_USER_ROLES, "write") ||
    (!hasDynamicPermissions() && ["owner", "admin"].includes(userData?.role));

  useEffect(() => {
    setPageTitle(t("Settings"));
    setBreadcrumbs([t("Settings"), t("User Roles")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const handleEditClick = (e, item) => {
    e.preventDefault();
    setSelectedItem(item);
    setShowEditItem(true);
  };

  return (
    <Layout title={t("Settings")} breadcrumbs={t("User Roles")}>
      {showCreateItem && (
        <CreateRoleForm
          onCreate={() => {
            setShowCreateItem(false);
            setNewItemAdded(true);
          }}
          onClose={() => setShowCreateItem(false)}
        />
      )}
      {newItemAdded && (
        <MessagePopup
          title={t("Role Created!")}
          message={t("The role has been successfully created!")}
          onClose={() => setNewItemAdded(false)}
        />
      )}
      {showEditItem && (
        <EditRoleForm
          role={selectedItem}
          onClose={() => setShowEditItem(false)}
          onUpdate={() => {
            setShowEditItem(false);
            setItemUpdated(true);
          }}
        />
      )}
      {itemUpdated && (
        <MessagePopup
          title={t("Role Updated!")}
          message={t("The role has been successfully updated!")}
          onClose={() => setItemUpdated(false)}
        />
      )}

      <DataTable
        filtersTop={
          <FilterContextProvider name="s">
            <SearchFilter placeholderText={t("Search by name")} />
          </FilterContextProvider>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="status">
              <SelectFilter
                options={[
                  { value: "active", label: t("Active") },
                  { value: "inactive", label: t("Inactive") },
                ]}
                placeholder={<div>{t("Status")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          canManageRoles && (
            <button
              onClick={() => setShowCreateItem(true)}
              className="btn btn-primary btn-medium"
            >
              {t("Create Role")}
            </button>
          )
        }
        columns={[
          { header: t("Role ID"), accessor: "id" },
          { header: t("Role Name"), accessor: "name" },
          { header: t("Description"), accessor: "description" },
          { header: t("Active Users"), accessor: "user_count" },
          { header: t("Status"), accessor: "is_active" },
          { header: t("Action"), accessor: "actions" },
        ]}
        rows={data.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) =>
          canManageRoles && (
            <Link to="#" onClick={(e) => handleEditClick(e, item)}>
              <CustomTooltip icon={<EditIcon />} message={t("Edit")} />
            </Link>
          )
        }
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "is_active":
              return (
                <Chip
                  state={value ? "active" : "inactive"}
                  label={value ? t("Active") : t("Inactive")}
                  showAsTooltop
                />
              );
            case "description":
              return value || "-";
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
};

export default Index;
